import api from '../api/client'

/**
 * Native iOS push (APNs) registration — runs ONLY inside the Onyx HR iOS app
 * (Capacitor container). In a normal browser / PWA it's a no-op, so the web
 * bundle stays free of any Capacitor dependency: we reach the native plugin
 * through Capacitor's auto-injected bridge proxy (`window.Capacitor.Plugins`).
 *
 * On grant → register with APNs → POST the device token to the server (where
 * lib/apns.js delivers notifications). Tapping a push routes to its url.
 *
 * IMPORTANT: Capacitor's `addListener` is ASYNC — the listener isn't wired on
 * the native side until its promise resolves. The native `registration` event
 * fires fast after `register()`, so we MUST await every listener BEFORE calling
 * register(); otherwise the token event is emitted before the JS listener
 * exists and is lost silently (no token, no error — the exact symptom we hit).
 */
let _inited = false

// This device's APNs token — kept so a logout can hand it to the server to
// unregister (else the next user of the phone, or nobody, keeps receiving the
// signed-out user's notifications), and so a re-login in the same app session
// (the listeners are already live, register() isn't called again) re-homes it.
// Persisted: a logout right after launch may precede this launch's registration.
const TOKEN_KEY = 'hr_push_token'
let _token = null
function rememberToken(t) {
  _token = t || null
  try { if (_token) localStorage.setItem(TOKEN_KEY, _token) } catch (_) {}
}
/** The APNs token registered by this device, or null (browser / not granted). */
export function nativePushToken() {
  if (_token) return _token
  try { return localStorage.getItem(TOKEN_KEY) || null } catch (_) { return null }
}

// Bump on every change to this file so the diag tells us which bundle actually
// ran on the device (the service worker can serve a stale bundle on first
// reopen — without this stamp we can't tell old code from new).
const VER = 'await-v3'

// Temporary diagnostic: trace every step so we can see, on a device we can't
// inspect, exactly where registration stops. Best-effort, never throws.
function diag(step, extra) {
  try { api.post('/push/diag', { src: 'initNativePush', ver: VER, step, ...(extra || {}) }) } catch (_) {}
}

export async function initNativePush() {
  if (_inited) {
    // A sign-in after a logout in the same app session: logout unregistered the
    // token, so register it again for whoever is signed in now.
    const token = nativePushToken()
    if (token) { try { await api.post('/push/apns-register', { token }) } catch (_) { /* next launch retries */ } }
    return
  }
  const Cap = typeof window !== 'undefined' ? window.Capacitor : null
  const env = {
    hasCapacitor: !!Cap,
    isNative: !!Cap?.isNativePlatform?.(),
    platform: Cap?.getPlatform?.() || 'web',
    hasPushPlugin: !!Cap?.Plugins?.PushNotifications,
  }
  if (!Cap?.isNativePlatform?.() || Cap.getPlatform?.() !== 'ios') { diag('skip-not-native', env); return }
  const Push = Cap.Plugins?.PushNotifications
  if (!Push) { diag('skip-no-plugin', env); return }
  _inited = true
  diag('start', env)
  try {
    // 1) Wire ALL listeners first, and AWAIT them so they're live on the native
    //    side before any event can fire.
    await Push.addListener('registration', async ({ value }) => {
      diag('token-received', { len: (value || '').length })
      rememberToken(value)
      try {
        await api.post('/push/apns-register', { token: value })
        diag('token-posted', {})
      } catch (e) { diag('token-post-failed', { error: String(e?.message || e) }) }
    })
    await Push.addListener('registrationError', (err) => {
      diag('registrationError', { error: String(err?.error || err || 'unknown') })
    })
    await Push.addListener('pushNotificationActionPerformed', (ev) => {
      const url = ev?.notification?.data?.url
      if (url && url !== '/') { try { window.location.assign(url) } catch (_) {} }
    })

    // 2) Permission, then register — listeners are guaranteed ready.
    const perm = await Push.requestPermissions()
    diag('permission', { receive: perm?.receive })
    if (perm?.receive !== 'granted') return

    await Push.register()
    diag('register-called', {})
  } catch (e) { diag('threw', { error: String(e?.message || e) }) }
}
